import { useQueryClient } from "@tanstack/react-query";

import useDispatchStore from "@web-app/store/dispatchStore";
import { AppFilterType, BaseApp, Order, User } from "@web-app/types";

import { useSessionUser } from "../hooks/useAuth";

export type OrdersCacheData = {
  apps: Map<string, BaseApp>;
  assignees: Map<string, User>;
  orders: Map<string, Order>;
};

/**
 * Get the standardized cache key for dispatch page orders.
 * Manage page uses its own query key in useManagePageOrders
 * and is handled separately via ["orders-paginated"] prefix matching.
 */
export const getOrdersCacheKey = (
  appFilter: AppFilterType,
  userTimezone?: string,
) => {
  return ["orders", appFilter, userTimezone || "system"];
};

/**
 * Hook to provide orders cache management functions
 */
export const useOrdersCache = () => {
  const queryClient = useQueryClient();
  const appFilter = useDispatchStore((state) => state.appFilter);
  const { data: sessionUser } = useSessionUser();
  const userTimezone = sessionUser?.timezone;

  const getCachedOrders = (): OrdersCacheData | undefined => {
    const isManagePage = window.location.pathname === "/manage";

    if (isManagePage) {
      const queries = queryClient.getQueriesData<{
        data: Map<string, Order>;
        totalCount: number;
      }>({
        queryKey: ["orders-paginated"],
      });

      // Find first query with actual data
      const queryWithData = queries.find(([, data]) => data?.data);
      const ordersData = queryWithData?.[1];

      if (!ordersData?.data) {
        return undefined;
      }

      return { apps: new Map(), assignees: new Map(), orders: ordersData.data };
    }

    const cacheKey = getOrdersCacheKey(appFilter, userTimezone);
    const cachedData = queryClient.getQueryData(cacheKey);

    if (!cachedData) return undefined;

    // Handle both raw Map (from query) and wrapped OrdersCacheData (after cache update)
    const maybeWrapped = cachedData as unknown as OrdersCacheData;
    if (maybeWrapped.orders instanceof Map) {
      return maybeWrapped;
    }

    // It's a raw Map from the query
    if (cachedData instanceof Map) {
      return {
        apps: new Map(),
        assignees: new Map(),
        orders: cachedData as Map<string, Order>,
      };
    }

    return undefined;
  };

  /**
   * Update the orders cache data
   */
  const updateCachedOrders = (
    updater: (data: OrdersCacheData) => OrdersCacheData,
  ) => {
    const isManagePage = window.location.pathname === "/manage";
    const existingData = getCachedOrders();

    if (existingData) {
      const updatedData = updater(existingData);

      if (isManagePage) {
        const queries = queryClient.getQueriesData<{
          data: Map<string, Order>;
          totalCount: number;
        }>({
          queryKey: ["orders-paginated"],
        });

        const queryWithData = queries.find(([, data]) => data?.data);
        if (queryWithData) {
          const [actualKey, currentData] = queryWithData;
          if (currentData) {
            queryClient.setQueryData(actualKey, {
              data: updatedData.orders,
              totalCount: currentData.totalCount,
            });
          }
        }
      } else {
        const cacheKey = getOrdersCacheKey(appFilter, userTimezone);
        queryClient.setQueryData(cacheKey, updatedData);
      }
    }
  };

  /**
   * Invalidate orders cache
   */
  const invalidateOrdersCache = () => {
    const isManagePage = window.location.pathname === "/manage";

    if (isManagePage) {
      queryClient.invalidateQueries({
        queryKey: ["orders-paginated"],
      });
    } else {
      const queryKey = getOrdersCacheKey(appFilter, userTimezone);
      queryClient.invalidateQueries({ queryKey });
    }
  };

  /**
   * Add or update a single order in cache
   * Note: Assignees are fetched separately via useOrderAssignees hook
   */
  const upsertOrderInCache = (order: Order) => {
    updateCachedOrders((data) => {
      const updatedOrders = new Map(data.orders);
      updatedOrders.set(order.id, order);

      return {
        apps: data.apps,
        assignees: data.assignees,
        orders: updatedOrders,
      };
    });
  };

  /**
   * Remove an order from cache
   */
  const removeOrderFromCache = (orderId: string) => {
    updateCachedOrders((data) => {
      const updatedOrders = new Map(data.orders);
      updatedOrders.delete(orderId);

      return {
        apps: data.apps,
        assignees: data.assignees,
        orders: updatedOrders,
      };
    });
  };

  return {
    getCachedOrders,
    invalidateOrdersCache,
    removeOrderFromCache,
    updateCachedOrders,
    upsertOrderInCache,
  };
};
